import { faMagnifyingGlass, faRotate, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useState } from 'react'
import { ActionButton } from '~/team_tools/components/ActionButton'
import { BUILD_READS_WEAPON_BALANCE, NOT_READ_BY_BUILD } from '~/team_tools/components/buildReads'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { Balance, StatCell, Weapon, clearScope, clearStat, getBalance, setScope, setStat } from './api'

/**
 * Weapon stats, on the website.
 *
 * Retuning a weapon used to mean a game build, then Django admin. Neither is
 * where the people who balance the game work, and a wrong number here changes
 * every paid match on the fleet within a heartbeat -- so the screen's job is
 * not to make typing easy, it is to make what is actually in force obvious.
 *
 * Hence three columns per stat rather than one box: what the build compiles
 * in, what the fleet says, and what this server says. Reading only the layer
 * you are editing is how somebody concludes a stat is unset when it is set one
 * level up. The effective value is spelled out with the layer it came from.
 *
 * Nothing here validates. Bounds, the "does this weapon have this stat" rule
 * and the cross-stat rules all belong to the registry, and its refusals are
 * printed word for word.
 */

/**
 * What each stat is called in English, and what it is measured in.
 *
 * The keys are the game's and cannot change -- they are what the server is
 * sent -- but "fire_interval" is not a thing anybody balancing a weapon
 * thinks in. The name leads and the key stays underneath it, so a number
 * typed here can still be talked about with the game team.
 */
const STAT_LABELS: Record<string, { label: string; unit?: string; note?: string }> = {
    max_damage: {
        label: 'Close-range damage',
        unit: 'hp',
        note: 'What a hit does up close. A shotgun splits this across its pellets.',
    },
    min_damage: {
        label: 'Long-range damage',
        unit: 'hp',
        note: 'What a hit does once distance has worn it all the way down.',
    },
    full_damage_range: {
        label: 'Damage starts dropping at',
        unit: 'm',
        note: 'Closer than this, every hit does the full close-range damage.',
    },
    max_range: {
        label: 'Maximum range',
        unit: 'm',
        note: 'Damage has fallen to the long-range number here. Past it, a shot does nothing.',
    },
    magazine: { label: 'Magazine size', unit: 'rounds' },
    fire_interval: { label: 'Time between shots', unit: 's', note: 'Lower is faster.' },
    reload_time: { label: 'Reload time', unit: 's' },
    pellets: { label: 'Pellets per shot', note: 'Shotguns. One means a single bullet.' },
    spread: { label: 'Spread', unit: '°', note: 'How wide shots scatter. Zero is pinpoint.' },
    recoil_up: { label: 'Vertical kick' },
    recoil_side: { label: 'Sideways kick' },
    charge_multiplier: { label: 'Full-charge bonus', note: 'Bows: a full charge does (1 + this) x damage.' },
    projectile_velocity: { label: 'Projectile speed', note: 'How fast the shot travels to its target.' },
    melee_damage: { label: 'Melee damage', unit: 'hp', note: 'Melee weapons, and the rifle-butt bash on guns.' },
    defense_rate: { label: 'Melee block', note: 'How much a block absorbs. The Great Shield is 400.' },
}

/**
 * Damage and distance, said as one sentence.
 *
 * The four falloff stats are meaningless on their own -- "full damage out to
 * 12" answers nothing without knowing what full damage is, where it stops,
 * and what it drops to. Read together they are a simple fact about the gun,
 * so the screen states that fact in this weapon's own live numbers rather
 * than leaving four rows to be assembled in somebody's head.
 */
const falloffNote = (stats: StatCell[]) => {
    const at = (name: string) => stats.find((s) => s.stat === name)?.effective ?? null
    const [close, far, full, max] = [
        at('max_damage'),
        at('min_damage'),
        at('full_damage_range'),
        at('max_range'),
    ]
    if (close === null || full === null || max === null) return null
    return (
        `Full ${close} damage out to ${full} m` +
        (far !== null ? `, tapering to ${far} by ${max} m` : ` and falling off to ${max} m`) +
        `. Beyond ${max} m a shot does nothing at all.`
    )
}

/** Rounds per minute, which is how a fire rate is actually discussed. */
const perMinute = (seconds: number | null) =>
    seconds && seconds > 0 ? `${Math.round(60 / seconds).toLocaleString()} rounds/min` : null

/**
 * The columns, which depend on which layer is being edited.
 *
 * On the fleet, "all servers" and "running now" are the same number whenever
 * the fleet value is set, so the middle column is noise. On a server overlay
 * they genuinely differ, and seeing what you are departing from is the whole
 * point of an overlay.
 *
 * The build default stays in both. Without it a number tells you nothing
 * about whether it is the game's own or somebody's, and "remove" becomes a
 * button that reverts to a value you cannot see.
 */
/** The game's class names, as people say them. */
const TYPE_LABELS: Record<string, string> = {
    ShooterWeapon: 'Guns',
    MeleeWeapon: 'Melee',
    Archery: 'Bows',
    Defense: 'Shields',
}

const GRID = {
    fleet: 'grid-cols-[minmax(0,1fr),8rem,22rem]',
    server: 'grid-cols-[minmax(0,1fr),6rem,8rem,22rem]',
}

const SOURCE_STYLE: Record<string, string> = {
    server: 'text-violet-300',
    fleet: 'text-sky-300',
    default: 'text-white/40',
}

const STATE_STYLE: Record<string, string> = {
    applied: 'text-green-400',
    pending: 'text-amber-300',
    behind: 'text-red-400',
    'old build': 'text-white/30',
}

const ago = (iso: string | null) => {
    if (!iso) return 'never'
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
    if (s < 60) return `${s}s ago`
    if (s < 3600) return `${Math.round(s / 60)}m ago`
    if (s < 86400) return `${Math.round(s / 3600)}h ago`
    return `${Math.round(s / 86400)}d ago`
}

const show = (v: number | null) => (v === null || v === undefined ? '—' : String(v))

export const Weapons = () => {
    const { addNotification } = useNotification()
    const [data, setData] = useState<Balance | null>(null)
    const [loading, setLoading] = useState(true)
    /** Null is the fleet. A server id is that server's overlay. */
    const [server, setServer] = useState<number | null>(null)
    const [open, setOpen] = useState<number | null>(null)
    const [notes, setNotes] = useState('')
    const [search, setSearch] = useState('')
    /** Null is every class. */
    const [type, setType] = useState<string | null>(null)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const next = await getBalance(server)
            setData(next)
            setNotes(next.notes)
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setLoading(false)
        }
    }, [server])

    useEffect(() => {
        load()
    }, [load])

    const fleet = data?.fleet ?? []
    const applied = fleet.filter((f) => f.state === 'applied').length
    const behind = fleet.filter((f) => f.state === 'behind').length
    /** Weapons matching the box, and within each, the stats worth showing.
     *
     *  One box, two jobs. "pitbull" is a weapon and narrows the list; "recoil"
     *  is not a weapon at all but is the actual question ("what is recoil
     *  across the fleet?"), so it keeps every weapon that has one and shows
     *  only those rows. Searching stats is how somebody tunes one thing
     *  across eighteen guns without opening each in turn. */
    const query = search.trim().toLowerCase()
    const matches = (text: string) => text.toLowerCase().includes(query)
    const statMatches = (cell: StatCell) =>
        matches(cell.stat) || matches(STAT_LABELS[cell.stat]?.label ?? '')

    const types = [...new Set((data?.weapons ?? []).map((w) => w.type).filter(Boolean))]

    const shown = (data?.weapons ?? [])
        .filter((w) => type === null || w.type === type)
        .map((w) => {
            if (!query) return w
            const byWeapon = matches(w.name) || matches(String(w.weapon_id)) || matches(w.type ?? '')
            if (byWeapon) return w
            const stats = w.stats.filter(statMatches)
            return stats.length ? { ...w, stats } : null
        })
        .filter(Boolean) as Weapon[]
    /** A stat search is worth opening straight onto, since the rows are the
     *  point; a weapon search is not, because the list is the point. */
    const statSearch =
        query !== '' && shown.length > 0 && shown.every((w) => w.stats.length < (data?.weapons?.[0]?.stats.length ?? 99))

    const refused = fleet.flatMap((f) =>
        Object.entries(f.refused ?? {}).map(([key, why]) => ({
            server: f.name ?? f.slot ?? `#${f.id}`,
            key,
            why: String(why),
        }))
    )

    return (
        <div className='flex w-full flex-col gap-4 text-xs font-mono'>
            {!BUILD_READS_WEAPON_BALANCE && (
                <div className='rounded border border-amber-300/40 px-3 py-2 text-amber-300'>
                    {NOT_READ_BY_BUILD}. Numbers saved here are delivered, but that build applies none of them, so
                    players are on the stats built into the game. Editing still works and takes effect when a build
                    reads them.
                </div>
            )}
            <div className='flex flex-row flex-wrap items-center gap-x-4 gap-y-2'>
                <label className='flex flex-row items-center gap-2'>
                    <span className='text-[10px] uppercase tracking-widest text-white/40'>Editing</span>
                    <select
                        value={server ?? 'global'}
                        onChange={(e) => setServer(e.target.value === 'global' ? null : Number(e.target.value))}
                        className='rounded border border-white/25 bg-transparent px-2 py-0.5 text-xs text-white outline-none focus:border-white/50'
                    >
                        <option value='global' className='bg-neutral-900'>
                            The whole fleet
                        </option>
                        {(data?.servers ?? []).map((s) => (
                            <option key={s.id} value={s.id} className='bg-neutral-900'>
                                Just {s.name ?? s.slot ?? `#${s.id}`}
                            </option>
                        ))}
                    </select>
                </label>

                {server !== null && (
                    <span className='text-violet-300'>
                        Changes here run on this server only. Try a number for an evening before the fleet gets it.
                    </span>
                )}

                {behind > 0 && (
                    <span className='text-red-400'>
                        {behind} of {fleet.length} servers have not picked up the last change yet
                    </span>
                )}

                {types.map((t) => (
                    <button
                        key={t}
                        type='button'
                        onClick={() => setType(type === t ? null : t)}
                        className={`rounded border px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                            type === t
                                ? 'border-sky-400/70 bg-sky-400/10 text-sky-300'
                                : 'border-white/15 text-white/40 hover:border-white/30 hover:text-white/70'
                        }`}
                    >
                        {TYPE_LABELS[t] ?? t}
                    </button>
                ))}

                <label className='flex flex-row items-center gap-2 rounded border border-white/20 px-2 py-0.5 focus-within:border-white/40'>
                    <FontAwesomeIcon icon={faMagnifyingGlass} className='text-white/30' />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder='a weapon, or a stat'
                        className='w-56 bg-transparent text-xs outline-none placeholder:text-white/25'
                    />
                    {search !== '' && (
                        <button
                            type='button'
                            onClick={() => setSearch('')}
                            className='text-white/30 hover:text-white'
                            title='Clear'
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    )}
                </label>

                <span className='ml-auto flex flex-row items-center gap-3'>
                    <ActionButton onClick={async () => load()} title='Refresh'>
                        <FontAwesomeIcon icon={faRotate} spin={loading} />
                    </ActionButton>
                </span>
            </div>

            {/* Why these numbers were picked. Not sent to the game, so it
                does not bump the revision -- but it is the difference between
                a value somebody can undo with confidence and one nobody
                dares touch. */}
            <div className='flex flex-row flex-wrap items-center gap-3'>
                <span className='text-[10px] uppercase tracking-widest text-white/40'>Why</span>
                <input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder='e.g. slower TTK for the Friday playtest'
                    maxLength={2000}
                    className='min-w-0 flex-1 rounded border border-white/20 bg-transparent px-2 py-0.5 text-xs outline-none focus:border-white/40'
                />
                {notes !== (data?.notes ?? '') && (
                    <button
                        type='button'
                        onClick={async () => {
                            try {
                                await setScope({ server, notes })
                                addNotification('Note saved', 'success')
                                load()
                            } catch (err: any) {
                                addNotification(err.message, 'error')
                            }
                        }}
                        className='rounded bg-violet-600/80 px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest hover:bg-violet-600'
                    >
                        save note
                    </button>
                )}
                {data?.removable && server !== null && (
                    <button
                        type='button'
                        onClick={async () => {
                            try {
                                const res = await clearScope(server)
                                addNotification(
                                    `${res.removed} override(s) dropped — this server is back on the fleet`,
                                    'success'
                                )
                                load()
                            } catch (err: any) {
                                addNotification(err.message, 'error')
                            }
                        }}
                        className='rounded border border-red-400/50 px-3 py-0.5 text-[10px] font-bold uppercase tracking-widest text-red-400 hover:bg-red-400/10'
                    >
                        drop this server's overrides
                    </button>
                )}
            </div>

            {/* The game refuses the pair, not the save that caused it, so a
                fault here means something already shipped is not in force. */}
            {(data?.problems?.length ?? 0) > 0 && (
                <div className='flex flex-col gap-1 rounded border border-red-400/50 px-3 py-2 text-red-400'>
                    <span className='font-bold uppercase tracking-wide'>
                        <FontAwesomeIcon icon={faTriangleExclamation} /> The game will refuse these
                    </span>
                    {data!.problems.map((p, i) => (
                        <span key={i}>{p}</span>
                    ))}
                </div>
            )}

            {!data?.build && (
                <div className='rounded border border-amber-400/40 px-3 py-2 text-amber-300'>
                    The build's own stat dump has not landed, so there is nothing to compare against.
                    <span className='ml-2 text-white/40'>
                        Values still save, but the default column reads "—" rather than guessing.
                    </span>
                </div>
            )}

            <div className='flex flex-col gap-2'>
                {shown.map((w) => (
                    <WeaponRow
                        key={w.weapon_id}
                        weapon={w}
                        scope={server}
                        // A stat search opens every match: the rows are what
                        // was asked for, and expanding eighteen by hand is
                        // the work the box was meant to save.
                        open={statSearch || open === w.weapon_id}
                        onToggle={() => setOpen(open === w.weapon_id ? null : w.weapon_id)}
                        onChanged={load}
                    />
                ))}
                {shown.length === 0 && (
                    <span className='text-white/30'>
                        {query !== ''
                            ? `No weapon or stat matches "${search.trim()}"`
                            : 'No weapon of that kind.'}
                    </span>
                )}
            </div>

            <span className='text-white/30'>
                {data?.build ? `Defaults from build ${data.build}. ` : ''}
                On revision {data?.revision ?? '—'}, changed {ago(data?.changed_at ?? null)}
                {fleet.length > 0 ? ` · ${applied}/${fleet.length} servers on it` : ''}.
            </span>

            {refused.length > 0 && (
                <div className='flex flex-col gap-1 rounded border border-red-400/50 px-3 py-2'>
                    <span className='font-bold uppercase tracking-wide text-red-400'>
                        <FontAwesomeIcon icon={faTriangleExclamation} /> Sent, and the server would not apply it
                    </span>
                    <span className='text-white/40'>
                        These bounds mirror the game's own, so this is the two having drifted apart rather than
                        anybody typing badly — most likely the build running is not the one the defaults came from
                        {data?.build ? ` (${data.build})` : ''}.
                    </span>
                    {refused.map(({ server: name, key, why }) => (
                        <span key={`${name}${key}`}>
                            <span className='text-white/60'>{name}</span> <span className='text-red-400'>{key}</span>{' '}
                            <span className='text-white/40'>{why}</span>
                        </span>
                    ))}
                </div>
            )}

            {fleet.length > 0 && (
                <div className='flex flex-row flex-wrap gap-1.5'>
                    {fleet.map((r) => (
                        <span
                            key={r.id}
                            title={`echoed r${r.echoed ?? '—'}`}
                            className={`rounded border px-2 py-0.5 ${
                                Object.keys(r.refused ?? {}).length ? 'border-red-400/50' : 'border-white/10'
                            }`}
                        >
                            <span className='text-white/60'>{r.name ?? r.slot}</span>{' '}
                            <span className={STATE_STYLE[r.state] ?? 'text-white/30'}>{r.state}</span>
                        </span>
                    ))}
                </div>
            )}
        </div>
    )
}

const WeaponRow = ({
    weapon,
    scope,
    open,
    onToggle,
    onChanged,
}: {
    weapon: Weapon
    scope: number | null
    open: boolean
    onToggle: () => void
    onChanged: () => void
}) => {
    const tuned = weapon.stats.filter((s) => s.exists && s.enabled).length
    const broken = weapon.stats.some((s) => s.deliverable_error)
    const falloff = falloffNote(weapon.stats)

    return (
        <div className={`rounded border ${broken ? 'border-red-400/40' : 'border-white/10'}`}>
            <button
                type='button'
                onClick={onToggle}
                className='flex w-full flex-row items-center gap-3 px-3 py-2 text-left hover:bg-white/5'
            >
                {/* A still, not the collection's gif: this is a list to
                    read down, and 18 looping weapons is a fairground. */}
                {weapon.image_url ? (
                    <img
                        src={weapon.image_url}
                        alt=''
                        loading='lazy'
                        className='h-20 w-32 shrink-0 rounded bg-black/40 object-contain'
                    />
                ) : (
                    <span className='h-20 w-32 shrink-0 rounded border border-white/10' />
                )}
                <span className='w-10 shrink-0 tabular-nums text-white/30'>{weapon.weapon_id}</span>
                <span className='text-lg font-bold'>{weapon.name}</span>
                <span className='ml-auto text-white/40'>
                    {tuned === 0 ? 'on the build' : `${tuned} stat${tuned === 1 ? '' : 's'} retuned here`}
                </span>
            </button>
            {open && (
                <div className='flex flex-col gap-1 border-t border-white/10 p-3'>
                    <div
                        className={`grid ${
                            scope === null ? GRID.fleet : GRID.server
                        } gap-3 text-[10px] uppercase tracking-widest text-white/30`}
                    >
                        <span>What it does</span>
                        {scope !== null && (
                            <span className='text-right' title='What every other server is set to'>
                                All servers
                            </span>
                        )}
                        {/* "Running now" claimed players were getting this. It
                            is our own saved snapshot, and the current build
                            applies none of it (CFG-50). */}
                        <span
                            className='text-right'
                            title={
                                BUILD_READS_WEAPON_BALANCE
                                    ? 'What players are actually getting right now'
                                    : `${NOT_READ_BY_BUILD}; players get the stats built into the game`
                            }
                        >
                            {BUILD_READS_WEAPON_BALANCE ? 'Running now' : 'Saved value'}
                        </span>
                        <span>{scope === null ? 'Change it for every server' : 'Change it for this server'}</span>
                    </div>
                    {falloff && (
                        <span className='rounded border border-white/10 px-3 py-2 text-white/60'>
                            {falloff}
                            <span className='ml-2 text-white/30'>
                                Full damage out to must stay below maximum range, so a save that crosses them is
                                refused.
                            </span>
                        </span>
                    )}

                    {/* A melee weapon has no magazine and no spread. Said
                        here, because two rows where another weapon has
                        thirteen reads as a screen that failed to load. */}
                    <span className='text-[10px] text-white/30'>
                        {weapon.type ? `${weapon.type}: ` : ''}
                        the build gives this weapon {weapon.stats.length} stat
                        {weapon.stats.length === 1 ? '' : 's'}. Anything else would be ignored by the game.
                    </span>
                    {weapon.stats.map((cell) => (
                        <StatRow
                            key={cell.stat}
                            weaponId={weapon.weapon_id}
                            cell={cell}
                            scope={scope}
                            onChanged={onChanged}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

const StatRow = ({
    weaponId,
    cell,
    scope,
    onChanged,
}: {
    weaponId: number
    cell: StatCell
    scope: number | null
    onChanged: () => void
}) => {
    const { addNotification } = useNotification()
    const [draft, setDraft] = useState(cell.value ?? '')
    const [busy, setBusy] = useState(false)

    // A fresh load -- someone else saved, or the scope changed -- wins over an
    // untouched box.
    useEffect(() => {
        setDraft(cell.value ?? '')
    }, [cell.value, cell.updated_at, scope])

    const dirty = draft.trim() !== (cell.value ?? '')
    const named = STAT_LABELS[cell.stat] ?? { label: cell.stat }
    const rpm = perMinute(cell.effective)

    const save = async (body: { value?: string; enabled?: boolean }) => {
        setBusy(true)
        try {
            const res = await setStat(weaponId, cell.stat, { ...body, server: scope })
            addNotification(
                res.unchanged ? 'Nothing changed' : `${cell.stat} saved — now on r${res.revision}`,
                'success'
            )
            onChanged()
        } catch (err: any) {
            // The registry's own words, not a second vaguer sentence here.
            addNotification(err.message, 'error')
        } finally {
            setBusy(false)
        }
    }

    const remove = async () => {
        setBusy(true)
        try {
            await clearStat(weaponId, cell.stat, scope)
            addNotification(`${cell.stat} back to ${scope === null ? 'the build' : 'the fleet'}`, 'success')
            onChanged()
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setBusy(false)
        }
    }

    return (
        <div
            className={`grid ${
                scope === null ? GRID.fleet : GRID.server
            } items-center gap-3 border-t border-white/5 py-1`}
        >
            <span className='flex min-w-0 flex-col' title={`${cell.help} (${cell.range})`}>
                <span className='truncate'>{named.label}</span>
                <span className='truncate text-[10px] text-white/25'>
                    {cell.stat}
                    {named.unit ? ` · ${named.unit}` : ''}
                    {cell.stat === 'fire_interval' && rpm ? ` · ${rpm}` : ''}
                </span>
                {named.note && <span className='text-[10px] text-white/30'>{named.note}</span>}
            </span>
            {scope !== null && (
                <span className='text-right tabular-nums text-sky-300'>{show(cell.fleet)}</span>
            )}
            {/* What players are getting, and nothing else. The build's own
                number is still what "remove" falls back to, but it is not a
                thing anybody tuning a weapon is reading, so it lives on the
                hover rather than on the screen. */}
            <span
                className={`text-right tabular-nums font-bold ${SOURCE_STYLE[cell.source]}`}
                title={cell.default !== null ? `The game ships with ${cell.default}` : undefined}
            >
                {show(cell.effective)}
            </span>
            <span className='flex flex-row flex-wrap items-center gap-2'>
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={cell.range}
                    disabled={busy}
                    className='w-24 rounded border border-white/20 bg-transparent px-2 py-0.5 text-right text-xs tabular-nums outline-none focus:border-white/40 disabled:opacity-40'
                />
                {dirty && draft.trim() !== '' && (
                    <button
                        type='button'
                        onClick={() => save({ value: draft.trim(), enabled: true })}
                        disabled={busy}
                        className='rounded bg-violet-600/80 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest hover:bg-violet-600 disabled:opacity-30'
                    >
                        save
                    </button>
                )}
                {cell.exists && (
                    <>
                        <button
                            type='button'
                            onClick={() => save({ enabled: !cell.enabled })}
                            disabled={busy}
                            className='text-[10px] uppercase tracking-widest text-white/40 underline hover:text-white'
                        >
                            {cell.enabled ? 'switch off' : 'switch on'}
                        </button>
                        <button
                            type='button'
                            onClick={remove}
                            disabled={busy}
                            className='text-[10px] uppercase tracking-widest text-red-400 underline hover:text-red-300'
                        >
                            remove
                        </button>
                    </>
                )}
                {cell.deliverable_error && <span className='text-red-400'>{cell.deliverable_error}</span>}
                {cell.exists && !cell.enabled && <span className='text-white/30'>off — the build's value ships</span>}
                {cell.source === 'server' && scope === null && (
                    <span className='text-violet-300'>a server overrides this</span>
                )}
                <span className='ml-auto text-white/25'>
                    {cell.updated_by ? `${cell.updated_by} · ${ago(cell.updated_at)}` : ''}
                </span>
            </span>
        </div>
    )
}
